import { Plus, Trash2 } from "lucide-react";

const inputCls = "field-underline w-full font-dm text-[#FAFAFA] py-2.5 text-sm placeholder:text-[#A3A3A3]/50";
const labelCls = "font-oswald uppercase tracking-[0.2em] text-[11px] text-[#A3A3A3] block mb-1";
const addBtnCls = "font-oswald uppercase tracking-[0.15em] text-[11px] border border-white/20 text-[#A3A3A3] px-3 py-2 hover:border-[#E85A24] hover:text-[#E85A24] transition-colors inline-flex items-center gap-2";

function Field({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type={type}
        min={type === "number" ? 0 : undefined}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(event) => onChange(type === "number" ? Number(event.target.value) : event.target.value)}
        className={inputCls}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div className="sm:col-span-2">
      <label className={labelCls}>{label}</label>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className={`${inputCls} resize-y`}
      />
    </div>
  );
}

function Group({ title, onAdd, addLabel, children }) {
  return (
    <div className="sm:col-span-2 border border-white/10 p-4 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h4 className="font-oswald uppercase tracking-[0.2em] text-xs text-[#FAFAFA]">{title}</h4>
        {onAdd && (
          <button type="button" onClick={onAdd} className={addBtnCls}>
            <Plus size={13} /> {addLabel || "Add"}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function RemoveButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-2 text-[#A3A3A3] hover:text-red-400 transition-colors shrink-0"
      aria-label={label}
    >
      <Trash2 size={15} />
    </button>
  );
}

export default function SectionMetadataFields({ sectionKey, metadata = {}, onChange }) {
  const set = (key, value) => onChange({ ...metadata, [key]: value });

  const updateItem = (key, index, patch) => {
    const rows = [...(metadata[key] || [])];
    rows[index] = { ...rows[index], ...patch };
    set(key, rows);
  };

  const removeItem = (key, index) => {
    set(key, (metadata[key] || []).filter((_, rowIndex) => rowIndex !== index));
  };

  const updateString = (key, index, value) => {
    const rows = [...(metadata[key] || [])];
    rows[index] = value;
    set(key, rows);
  };

  const removeString = (key, index) => {
    set(key, (metadata[key] || []).filter((_, rowIndex) => rowIndex !== index));
  };

  if (sectionKey === "navigation") {
    const links = metadata.links || [];
    return (
      <Group title="Navigation links" addLabel="Add Link" onAdd={() => set("links", [...links, { label: "", href: "" }])}>
        {links.map((link, index) => (
          <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <Field label="Label" value={link.label} onChange={(value) => updateItem("links", index, { label: value })} />
            <Field label="Link / anchor" value={link.href} onChange={(value) => updateItem("links", index, { href: value })} placeholder="#events" />
            <RemoveButton onClick={() => removeItem("links", index)} label="Remove navigation link" />
          </div>
        ))}
        {links.length === 0 && <p className="font-dm text-xs text-[#A3A3A3]">No navigation links.</p>}
      </Group>
    );
  }

  if (sectionKey === "hero") {
    return (
      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-white/10 p-4">
        <Field label="Image alt text" value={metadata.image_alt} onChange={(value) => set("image_alt", value)} />
        <Field label="Scroll label" value={metadata.scroll_label} onChange={(value) => set("scroll_label", value)} />
      </div>
    );
  }

  if (sectionKey === "marquee") {
    const words = metadata.words || [];
    return (
      <Group title="Marquee words" addLabel="Add Word" onAdd={() => set("words", [...words, ""])}>
        {words.map((word, index) => (
          <div key={index} className="flex items-end gap-3">
            <div className="flex-1">
              <Field label={`Word ${index + 1}`} value={word} onChange={(value) => updateString("words", index, value)} />
            </div>
            <RemoveButton onClick={() => removeString("words", index)} label="Remove marquee word" />
          </div>
        ))}
      </Group>
    );
  }

  if (sectionKey === "events") {
    return (
      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-white/10 p-4">
        <Field label="Section number" value={metadata.section_index} onChange={(value) => set("section_index", value)} />
        <Field label="Maximum events shown" type="number" value={metadata.max_items} onChange={(value) => set("max_items", value)} />
        <Field label="Loading text" value={metadata.loading_text} onChange={(value) => set("loading_text", value)} />
        <Field label="Ticket button label" value={metadata.tickets_label} onChange={(value) => set("tickets_label", value)} />
      </div>
    );
  }

  if (sectionKey === "roster") {
    return (
      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-white/10 p-4">
        <Field label="Section number" value={metadata.section_index} onChange={(value) => set("section_index", value)} />
        <Field label="Maximum fighters shown" type="number" value={metadata.max_items} onChange={(value) => set("max_items", value)} />
        <div className="sm:col-span-2">
          <Field label="Loading text" value={metadata.loading_text} onChange={(value) => set("loading_text", value)} />
        </div>
      </div>
    );
  }

  if (sectionKey === "manifesto") {
    const chapters = metadata.chapters || [];
    const stats = metadata.stats || [];

    return (
      <>
        <div className="sm:col-span-2">
          <Field label="Section number" value={metadata.section_index} onChange={(value) => set("section_index", value)} />
        </div>

        <Group
          title="Manifesto chapters"
          addLabel="Add Chapter"
          onAdd={() => set("chapters", [...chapters, { number: String(chapters.length + 1).padStart(2, "0"), title: "", body: "" }])}
        >
          {chapters.map((chapter, index) => (
            <div key={index} className="border-t border-white/10 first:border-t-0 pt-4 first:pt-0">
              <div className="grid grid-cols-[90px_1fr_auto] gap-3 items-end">
                <Field label="Number" value={chapter.number} onChange={(value) => updateItem("chapters", index, { number: value })} />
                <Field label="Title" value={chapter.title} onChange={(value) => updateItem("chapters", index, { title: value })} />
                <RemoveButton onClick={() => removeItem("chapters", index)} label="Remove manifesto chapter" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <TextArea label="Chapter text" value={chapter.body} onChange={(value) => updateItem("chapters", index, { body: value })} rows={4} />
              </div>
            </div>
          ))}
        </Group>

        <Group
          title="Manifesto statistics"
          addLabel="Add Statistic"
          onAdd={() => set("stats", [...stats, { value: "", label: "" }])}
        >
          {stats.map((stat, index) => (
            <div key={index} className="grid grid-cols-[110px_1fr_auto] gap-3 items-end">
              <Field label="Value" value={stat.value} onChange={(value) => updateItem("stats", index, { value })} />
              <Field label="Label" value={stat.label} onChange={(value) => updateItem("stats", index, { label: value })} />
              <RemoveButton onClick={() => removeItem("stats", index)} label="Remove statistic" />
            </div>
          ))}
        </Group>
      </>
    );
  }

  if (sectionKey === "join") {
    const interests = metadata.interests || [];
    return (
      <>
        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-white/10 p-4">
          <Field label="Section number" value={metadata.section_index} onChange={(value) => set("section_index", value)} />
          <Field label="Newsletter email placeholder" value={metadata.email_placeholder} onChange={(value) => set("email_placeholder", value)} />
        </div>

        <Group title="Contact interests" addLabel="Add Interest" onAdd={() => set("interests", [...interests, ""])}>
          {interests.map((interest, index) => (
            <div key={index} className="flex items-end gap-3">
              <div className="flex-1">
                <Field label={`Interest ${index + 1}`} value={interest} onChange={(value) => updateString("interests", index, value)} />
              </div>
              <RemoveButton onClick={() => removeString("interests", index)} label="Remove contact interest" />
            </div>
          ))}
        </Group>

        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-white/10 p-4">
          <Field label="Contact title" value={metadata.contact_title} onChange={(value) => set("contact_title", value)} />
          <Field label="Contact title accent" value={metadata.contact_title_accent} onChange={(value) => set("contact_title_accent", value)} />
          <TextArea label="Contact description" value={metadata.contact_body} onChange={(value) => set("contact_body", value)} rows={3} />
          <Field label="Contact button label" value={metadata.contact_button_label} onChange={(value) => set("contact_button_label", value)} />
          <Field label="Name placeholder" value={metadata.name_placeholder} onChange={(value) => set("name_placeholder", value)} />
          <Field label="Email placeholder" value={metadata.contact_email_placeholder} onChange={(value) => set("contact_email_placeholder", value)} />
          <div className="sm:col-span-2">
            <Field label="Message placeholder" value={metadata.message_placeholder} onChange={(value) => set("message_placeholder", value)} />
          </div>
        </div>
      </>
    );
  }

  if (sectionKey === "sponsors" || sectionKey === "team") {
    return (
      <div className="sm:col-span-2">
        <Field label="Section number" value={metadata.section_index} onChange={(value) => set("section_index", value)} />
      </div>
    );
  }

  if (sectionKey === "footer") {
    const socials = metadata.socials || [];
    return (
      <>
        <div className="sm:col-span-2">
          <Field label="Footer tagline" value={metadata.tagline} onChange={(value) => set("tagline", value)} />
        </div>
        <Group title="Social links" addLabel="Add Social Link" onAdd={() => set("socials", [...socials, { label: "", url: "" }])}>
          {socials.map((social, index) => (
            <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <Field label="Label" value={social.label} onChange={(value) => updateItem("socials", index, { label: value })} />
              <Field label="URL" type="url" value={social.url} onChange={(value) => updateItem("socials", index, { url: value })} />
              <RemoveButton onClick={() => removeItem("socials", index)} label="Remove social link" />
            </div>
          ))}
        </Group>
      </>
    );
  }

  return (
    <div className="sm:col-span-2 border border-white/10 p-4">
      <p className="font-dm text-xs text-[#A3A3A3]">
        This custom section key has no additional structured fields. Its standard title, body, button and image fields remain editable above.
      </p>
    </div>
  );
}
